import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsify } from '../utils/url';
import { renderEtaLabel, useNow } from '../utils/eta';
import { movieClips } from '../utils/movie';
import { getAssetPlaybackUrl } from '../data/uploadService';
import { useAuth } from '../AuthContext';
import HoverVideo from './HoverVideo';

// captionExtra: optional inline pills after the title (version tags, family
// expand toggle, pin/unpin) — rendered inside the caption so they never
// overlap the title text.
export default function MovieCard({ movie, captionExtra = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const rStatus = movie.render?.status;
  const rendering = rStatus === 'rendering' || rStatus === 'queued';
  const now = useNow(rendering);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const needsVideo =
    movie.status !== 'uploading' &&
    !(movie.clips && movie.clips.length) &&
    !movie.videoUrl &&
    !movie.streamUrl;
  const thumbSrc =
    movie.thumbUrl || movie.hero?.posterUrl || movie.heroUrl || '';
  const hasThumb = thumbSrc && !imgFailed;

  // Hover-preview source: the published stream if ready, else the first clip.
  const resolvePreview = useCallback(async () => {
    if (movie.render?.status === 'ready' && movie.streamUrl) {
      return { url: httpsify(movie.streamUrl), hls: true };
    }
    const first = movieClips(movie)[0];
    if (!first) return null;
    if (first.url) return { url: httpsify(first.url), hls: /\.m3u8(\?|$)/.test(first.url) };
    if (first.s3Key) {
      const u = await getAssetPlaybackUrl(first.s3Key);
      return u ? { url: u, hls: false } : null;
    }
    return null;
  }, [movie]);

  // Bake provenance for the caption: after a day of iterating bakes, the card
  // must answer "which bake is this" at a glance.
  const bakedMs = movie.render?.status === 'ready' && movie.render?.finishedAt ? movie.render.finishedAt : 0;
  const timeLabel = bakedMs
    ? `Baked ${new Date(bakedMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
    : rendering
      ? 'Baking now…'
      : 'Draft — not baked';

  return (
    <div className="cs-movie-cell">
    <div className="cs-movie-caption" onClick={() => navigate(`/watch/${movie.id}`)}>
      {movie.title}
      {captionExtra}
      <span className="cs-movie-time">{timeLabel}</span>
    </div>
    <HoverVideo
      className="cs-card"
      resolve={resolvePreview}
      disabled={movie.status === 'uploading' || needsVideo}
      onClick={() => navigate(`/watch/${movie.id}`)}
    >
      {hasThumb ? (
        <>
          {!imgLoaded && <div className="cs-card-shimmer" aria-hidden />}
          <img
            src={httpsify(thumbSrc)}
            alt={movie.title}
            loading="lazy"
            style={{ opacity: imgLoaded ? 1 : 0 }}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
          />
        </>
      ) : (
        <div className="cs-card-fallback">{movie.title}</div>
      )}
      {needsVideo && !rendering && (
        <div className="cs-card-flag">Needs video</div>
      )}
      {rendering && (
        <div
          className="cs-card-flag"
          style={{ background: 'rgba(20,224,200,0.9)', color: '#04201c' }}
          title={renderEtaLabel(movie.render, now)}
        >
          {rStatus === 'queued' ? 'Queued…' : `Publishing ${Math.round(movie.render?.progress || 0)}%`}
        </div>
      )}
      {user && (
        <button
          className="cs-card-edit"
          title="Edit movie"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/movies/${movie.id}/edit`);
          }}
        >
          ✎
        </button>
      )}
      <div className="cs-card-overlay">
        <div className="sub">
          {movie.status === 'uploading'
            ? 'Uploading…'
            : [movie.location, movie.year].filter(Boolean).join(' · ')}
        </div>
      </div>
    </HoverVideo>
    </div>
  );
}
